import { HashLocationStrategy, LocationStrategy } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { I18NEXT_NAMESPACE, I18NextModule } from 'angular-i18next';
import ChartsModule from 'app/CustomChatModule';
import { MomentLocaleInitService } from 'app/shared/locale/moment-locale-init.service';
import { DashboardModule } from 'app/views/dashboard/dashboard.module';
import 'hammerjs';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ToastModule } from 'primeng/toast';
import { environment } from '../environments/environment';
import { WindowToken, windowProvider } from '../testing/shared/mock-window';
import { AboutModule } from './about/about.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HbComponentLibraryModule } from './component-library/component-library.module';
import {
  AppAsideComponent,
  AppBreadcrumbsComponent,
  AppHeaderComponent,
  AppSidebarComponent,
  AppSidebarFooterComponent,
  AppSidebarFormComponent,
  AppSidebarHeaderComponent,
} from './components';
import { SubMenuComponent } from './components/app-sidebar/sub-menu/sub-menu.component';
import { BlankComponent } from './components/home/blank.component';
import { HomeComponent } from './components/home/home.component';
import { ConfigService } from './core/config.service';
import { CoreModule } from './core/core.module';
import { HttpInterceptorService } from './core/http-interceptor.service';
import { PermissionsService } from './core/permissions.service';
import { WINDOW, WINDOW_PROVIDERS } from './core/window-provider';
import { MENU_DIRECTIVES, SIDEBAR_TOGGLE_DIRECTIVES } from './directives';
import { FeaturesModule } from './features/features.module';
import { LicensingModule } from './licensing/licensing.module';
import { NotificationsModule } from './notifications/notifications.module';
import { PageNotFoundComponent } from './page-not-found.component';
import { AuthenticationModule } from './security/authentication/authentication.module';
import { DirtyFormDialogModule } from './shared/dirty-form-guard/dirty-form-dialog.module';
import { HbSharedModule } from './shared/hb-shared.module';
import { BatchJobStatePipe } from './shared/pipes/batch-job-state.pipe';
import { DateTimeFormatterPipe } from './shared/pipes/date-time-formatter.pipe';
import { GenericDictionaryPipe } from './shared/pipes/generic-dictionary.pipe';
import { ScenarioTypePipe } from './shared/pipes/scenario-type.pipe';
import { I18N_PROVIDERS } from './thirdparty/i18next/i18next';
import { UserlaneComponent } from './thirdparty/userlane/userlane.component';

// Import template components
const APP_COMPONENTS = [
  AppAsideComponent,
  AppBreadcrumbsComponent,
  AppHeaderComponent,
  AppSidebarComponent,
  AppSidebarFooterComponent,
  AppSidebarFormComponent,
  AppSidebarHeaderComponent,
];

// Import template directives
const APP_DIRECTIVES = [SIDEBAR_TOGGLE_DIRECTIVES, MENU_DIRECTIVES];

export const ConfigLoader = (configService: ConfigService, permissionService: PermissionsService) => () =>
  configService.load(environment.configFile, permissionService);
export const MomentLocaleInit = (momentLocaleInitService: MomentLocaleInitService) => () => momentLocaleInitService.init();

// Import 3rd party components

@NgModule({
  declarations: [
    AppComponent,
    PageNotFoundComponent,
    ...APP_COMPONENTS,
    ...APP_DIRECTIVES,
    HomeComponent,
    BlankComponent,
    SubMenuComponent,
    UserlaneComponent,
  ],
  imports: [
    AuthenticationModule,
    HbSharedModule,
    BrowserModule,
    BrowserAnimationsModule,
    CoreModule,
    ChartsModule,
    DashboardModule,
    FeaturesModule,
    LicensingModule,
    // Make sure that the AppRoutingModule appears AFTER any module imports that declare their own routing
    AppRoutingModule,
    AboutModule,
    HttpClientModule,
    NotificationsModule,
    ToastModule,
    FormsModule,
    HbComponentLibraryModule,
    DirtyFormDialogModule,
    I18NextModule.forRoot(),
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: ConfigLoader,
      deps: [ConfigService, PermissionsService, WINDOW],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: MomentLocaleInit,
      deps: [MomentLocaleInitService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpInterceptorService,
      deps: [ConfigService],
      multi: true,
    },
    {
      provide: LocationStrategy,
      useClass: HashLocationStrategy,
    },
    {
      provide: I18NEXT_NAMESPACE,
      useValue: ['error', 'module.dashboard', 'module.hb-shared', 'module.about', 'languages', 'module.licensing', 'module.security', 'module.component-library'],
    },
    WINDOW_PROVIDERS,
    NgForm,
    BsModalRef,
    { provide: WindowToken, useFactory: windowProvider },
    DateTimeFormatterPipe,
    ScenarioTypePipe,
    BatchJobStatePipe,
    GenericDictionaryPipe,
    I18N_PROVIDERS,
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }
